// The pieces of one application (a reservation) that the sheet's flat dialog and the ApplicationDrawer share — moved out of
// ReservationsTab.tsx as they were (docs/plans/lettings-one-form.md, phase 3): the review and rent chips, the phase and
// stage chips (the sheet tab), the documents, the rent request read-only (RentRequestInfo — deciding it is a step of the
// ApplicationDrawer), Goodlord referencing and the move-in checklist; the sheet's Dots. Since 26 Oct 2026 (later) the one
// roadmap — eight steps since 3 Nov 2026 (Qualify merged into Approve): RoadmapChip ("Where it is"), RoadmapDots (n/8), RoadmapStrip (the drawer's top) and NextLine (what
// is next, whose move) — the "Where it is" tracker list and the review summary box are gone. The tenant-page link's
// Open · Copy · Send email / Create the link are items of the drawer's Actions menu since 3 Nov 2026 (tenantLinkItems).
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, FileText, Lock, Trash2, Upload as UploadIcon } from 'lucide-react';
import { Button, Input, Field, type MenuEntry } from '../ui';
import { FormError } from '../forms/common';
import { formatDate, formatMoney } from '../../lib/viz';
import { checklistsApi } from '../../lib/checklists';
import {
  reservationsApi, fileToUpload, openStoredFile, REVIEW_LABEL,
  type Flat, type FlatFull, type Roadmap, type DocCategory, type PipelineRow, type ReviewStatus, type ReferencingOutcome, type GuarantorBody,
} from '../../lib/reservations';
import { PhoneInput } from '../ui/PhoneInput';
import { formatPhone } from '../../lib/phone';
import { fmtWhen } from '../../lib/dates';

const REVIEW_TONE: Record<ReviewStatus, string> = {
  SUBMITTED: 'bg-amber-50 text-amber-800', QUALIFIED: 'bg-amber-50 text-amber-800', APPROVED: 'bg-emerald-50 text-emerald-800', REJECTED: 'bg-red-50 text-red-700',
};
/** Where an application is in Lotus Living's review (Submitted — and a legacy Qualified — shows as "Under review"). */
export function ReviewChip({ status, className = '' }: { status: ReviewStatus | null; className?: string }) {
  if (!status) return null;
  return <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-[3px] text-[11px] font-medium ${REVIEW_TONE[status]} ${className}`}>{REVIEW_LABEL[status]}</span>;
}

/** A rent request on a row: "£1,900 asked · 5% under", a counter-offer, or the outcome. Nothing when none was made. */
export function RentChip({ r, className = '' }: { r: Pick<PipelineRow, 'rentRequestStatus' | 'requestedRentMinor' | 'requestedPercent' | 'rentCounterMinor' | 'rentMinor' | 'currency' | 'needsHigherApproval'>; className?: string }) {
  const st = r.rentRequestStatus;
  if (!st || st === 'NONE') return null;
  const m = (v: number | null) => (v === null ? '—' : formatMoney(v, r.currency));
  const text = st === 'PENDING' ? `${m(r.requestedRentMinor)} asked · ${r.requestedPercent ?? 0}% under`
    : st === 'COUNTERED' ? `Countered ${m(r.rentCounterMinor)}` : st === 'APPROVED' ? `Rent agreed ${m(r.rentMinor)}` : 'Full rent';
  const tone = st === 'PENDING' ? (r.needsHigherApproval ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800') : st === 'COUNTERED' ? 'bg-sky-50 text-sky-800' : 'bg-ink-100 text-ink-600';
  return <span title={r.needsHigherApproval ? 'Above your approval limit' : undefined} className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-[3px] text-[11px] font-medium ${tone} ${className}`}>{r.needsHigherApproval && <Lock className="h-2.5 w-2.5" />}{text}</span>;
}

/** The four stages everyone sees (the nine-stage detail is inside the flat). */
export function PhaseChip({ f, className = '' }: { f: Pick<Flat, 'phaseColour' | 'phaseLabel'>; className?: string }) {
  return <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-[3px] text-[11px] font-medium ${className}`} style={{ background: `${f.phaseColour}22`, color: f.phaseColour }}><i className="h-1.5 w-1.5 rounded-full" style={{ background: f.phaseColour }} />{f.phaseLabel}</span>;
}

/** The steps as dots in the stage colour, then "n/6" (the sheet's and Lettings → Applications' Steps column). */
export function Dots({ f }: { f: Pick<Flat, 'steps' | 'stepsDone' | 'stepsTotal' | 'stageColour'> }) {
  return (
    <div className="flex items-center gap-1" title={`${f.stepsDone} of ${f.stepsTotal} steps`}>
      {f.steps.map((s) => <i key={s.key} title={s.label} className="block h-1.5 w-3.5 rounded-full" style={{ background: s.done ? f.stageColour : 'var(--color-ink-200)' }} />)}
      <span className="ml-1 text-[10.5px] tabular-nums text-ink-400">{f.stepsDone}/{f.stepsTotal}</span>
    </div>
  );
}

export function StageChip({ f, className = '' }: { f: Pick<Flat, 'stageColour' | 'stageLabel'>; className?: string }) {
  return <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-[3px] text-[11px] font-medium ${className}`} style={{ background: `${f.stageColour}22`, color: f.stageColour }}><i className="h-1.5 w-1.5 rounded-full" style={{ background: f.stageColour }} />{f.stageLabel}</span>;
}

/** The roadmap's colour by where it has got to: the review amber, the tenant's steps blue, Lotus Living's violet, moved in green. */
const ROADMAP_TONE: Record<string, string> = {
  submitted: '#d9922e', approved: '#e0a33a', holding: '#5b7fd1', referencing: '#5b7fd1', signed: '#5b7fd1',
  money: '#8b6fd6', checklist: '#8b6fd6', moved: '#2b8a5c', rejected: '#b04a4a',
};
/** "Where it is" — the last roadmap step done (Submitted … Moved in), the same words in the table, the drawer and the portal. */
export function RoadmapChip({ r, className = '' }: { r: { roadmap: Roadmap | null }; className?: string }) {
  if (!r.roadmap) return null;
  const c = ROADMAP_TONE[r.roadmap.step] ?? '#6b7f95';
  return <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-[3px] text-[11px] font-medium ${className}`} style={{ background: `${c}1f`, color: c }}><i className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />{r.roadmap.label}</span>;
}
/** The eight roadmap steps as dots, then "n/8" (the Steps column). */
export function RoadmapDots({ roadmap }: { roadmap: Roadmap | null }) {
  if (!roadmap) return null;
  const c = ROADMAP_TONE[roadmap.step] ?? '#6b7f95';
  return (
    <div className="flex items-center gap-1" title={`${roadmap.done} of ${roadmap.total}${roadmap.next ? ` · next: ${roadmap.next.label}` : ''}`}>
      {roadmap.steps.map((s) => <i key={s.key} title={s.label} className="block h-1.5 w-2.5 rounded-full" style={{ background: s.done ? c : 'var(--color-ink-200)' }} />)}
      <span className="ml-1 text-[10.5px] tabular-nums text-ink-400">{roadmap.done}/{roadmap.total}</span>
    </div>
  );
}

/**
 * The roadmap strip at the top of the application drawer: Submitted → Approved → Holding deposit →
 * Referencing → Agreement signed → Deposit and first rent → Lotus Living internal checklist → Moved in. Done = a green
 * tick, the current (first not done) = a bronze ring, the rest locked (grey); a rejected application has no current step.
 * Sized to fit the eight steps in the half-screen drawer (3 Nov 2026); a phone scrolls it sideways.
 */
export function RoadmapStrip({ roadmap }: { roadmap: Roadmap }) {
  const current = roadmap.step === 'rejected' ? -1 : roadmap.steps.findIndex((s) => !s.done);
  const steps = roadmap.steps;
  return (
    <ol className="flex overflow-x-auto pb-1" aria-label="Where the application is">
      {steps.map((s, i) => {
        const state = s.done ? 'done' : i === current ? 'now' : 'later';
        return (
          <li key={s.key} className="flex min-w-[44px] flex-1 flex-col items-center gap-1 text-center">
            <span className="flex w-full items-center">
              <span className={`h-px flex-1 ${i === 0 ? 'bg-transparent' : steps[i - 1]!.done ? 'bg-emerald-400' : 'bg-ink-200'}`} />
              <span className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 ${state === 'done' ? 'border-emerald-500 bg-emerald-500 text-on-primary' : state === 'now' ? 'border-bronze-500 bg-bronze-50' : 'border-ink-200 bg-white text-ink-300'}`}>
                {state === 'done' ? <Check className="h-3 w-3" /> : state === 'now' ? <i className="h-1.5 w-1.5 rounded-full bg-bronze-500" /> : <Lock className="h-2.5 w-2.5" />}
              </span>
              <span className={`h-px flex-1 ${i === steps.length - 1 ? 'bg-transparent' : s.done ? 'bg-emerald-400' : 'bg-ink-200'}`} />
            </span>
            <span className={`max-w-full text-[10px] leading-tight tracking-tight ${state === 'done' ? 'text-emerald-700' : state === 'now' ? 'font-semibold text-bronze-700' : 'text-ink-400'}`}>{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Lotus Living's move on the current roadmap step by hand (the holding deposit landed, referencing passed by override,
 * the agreement sent / signed): the drawer puts it in the footer. Null when nothing is due from Lotus Living.
 */
export function manualStep(f: FlatFull, canManage: boolean, on: { tick: (key: string, done: boolean) => void; approveReferencing: (approved: boolean) => void }): { label: string; run: () => void } | null {
  const handed = f.status === 'HANDED_OVER';
  if (!canManage || handed || f.reviewStatus !== 'APPROVED') return null;
  const cur = f.roadmap.next?.key ?? null;
  const step = (k: string) => !!f.steps.find((s) => s.key === k)?.done;
  if (cur === 'holding') return { label: f.portal?.holdingDepositDeclaredAt ? 'Holding deposit received' : 'Mark the holding deposit received', run: () => on.tick('hd', true) };
  if (cur === 'referencing') return { label: 'Mark referencing passed (override)', run: () => on.approveReferencing(true) };
  if (cur === 'signed') return step('ten') ? { label: 'Mark the agreement signed', run: () => on.tick('sign', true) } : { label: 'Mark the agreement sent', run: () => on.tick('ten', true) };
  return null;
}

/**
 * One plain line under the roadmap — no card (user, 26 Oct 2026): what happens next and whose move it is (the API's
 * `next` / `nextBy`), the payment link last sent and the documents / checklist counts as links. The tenant-page link row
 * moved into the footer's Actions menu (3 Nov 2026, tenantLinkItems).
 */
export function NextLine({ f, canManage, onOpenTab }: { f: FlatFull; canManage: boolean; onOpenTab: (t: 'documents' | 'movein') => void }) {
  const handed = f.status === 'HANDED_OVER';
  const p = f.portal;
  const by = f.next.nextBy;
  const cur = f.roadmap.next?.key ?? null;
  const chase = by === 'tenant' ? `${f.applicant?.name ?? 'the tenant'}${f.applicant?.phone ? ` · ${formatPhone(f.applicant.phone)}` : ''}`
    : by === 'lotus' ? (canManage ? 'you (Lotus Living)' : f.lotusContact ? `${f.lotusContact.name} at Lotus Living${f.lotusContact.phone ? ` · ${formatPhone(f.lotusContact.phone)}` : ''}` : 'Lotus Living')
      : by === 'agent' ? f.agent?.name ?? 'the agent' : null;
  if (f.reviewStatus === 'REJECTED') return null;
  return (
    <div className="mt-3 space-y-1 text-[12.5px] text-ink-700">
      <div><b className="font-semibold text-ink-900">Next:</b> {f.next.next}{chase && <span className="text-ink-500"> — waiting on <b className="font-medium text-ink-800">{chase}</b></span>}</div>
      {cur === 'holding' && p?.holdingDepositDeclaredAt && <div className="text-ink-600">The tenant says they paid it on {formatDate(p.holdingDepositDeclaredAt)}.</div>}
      {cur === 'holding' && !handed && p?.holdingPaymentLink && <PaymentLinkLine link={p.holdingPaymentLink} />}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {cur === 'checklist' && f.moveIn && <button type="button" onClick={() => onOpenTab('movein')} className="text-bronze-700 hover:underline">{f.moveIn.done} of {f.moveIn.total} on the internal checklist</button>}
        {!f.docs.complete && f.reviewStatus === 'APPROVED' && <button type="button" onClick={() => onOpenTab('documents')} className="text-bronze-700 hover:underline">{f.docs.uploaded} of {f.docs.required} required documents</button>}
      </div>
    </div>
  );
}

/**
 * "Payment link sent 2 Nov 14:10 by Sophie Marsh · Open · Copy" — the holding deposit's payment link last emailed
 * (docs/plans/holding-deposit-payment-link.md). Sending it again is the footer's Send the link again.
 */
function PaymentLinkLine({ link }: { link: { url: string; sentAt: string; sentBy: string | null } }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(link.url); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { window.prompt('Copy the payment link', link.url); }
  };
  return (
    <div className="text-ink-600">
      Payment link sent {fmtWhen(link.sentAt)}{link.sentBy ? ` by ${link.sentBy}` : ''}
      {' · '}<a href={link.url} target="_blank" rel="noopener noreferrer" className="text-bronze-700 hover:underline" title={link.url}>Open</a>
      {' · '}<button type="button" onClick={() => void copy()} className="text-bronze-700 hover:underline" title={link.url}>{copied ? 'Copied' : 'Copy'}</button>
    </div>
  );
}

/**
 * The documents: one card per category — green once a file is on it, amber while a required one is missing, white when
 * optional. 3 Nov 2026 (the user: titles and noise off, the content stays): no "Still needed" line, no tick box and no
 * Upload button on show. Where the caller may upload, the whole card picks a file on click, takes a file dropped on it
 * (a dashed outline while dragging over), and shows a small Upload / Add another on its top edge on hover or focus — on
 * the border, so it never covers the label.
 */
export function DocumentsTab({ f, cats: allCats, canUpload, canManage, onChange }: { f: FlatFull; cats: DocCategory[]; canUpload: boolean; canManage: boolean; compact?: boolean; onChange: (r: FlatFull) => void }) {
  // British and Irish: the passport is their right to rent evidence, so the right to rent document is optional for them.
  const cats = allCats.map((c) => (c.key === 'rtr' && f.rtrDocNeeded === false ? { ...c, required: false } : c));
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const up = async (category: string, file: File | undefined) => {
    if (!file) return;
    setBusy(category); setError(null);
    try { onChange(await reservationsApi.upload(f.id, { category, ...(await fileToUpload(file)) })); } catch (e) { setError(e); } finally { setBusy(null); }
  };
  const del = async (docId: string) => { setError(null); try { onChange(await reservationsApi.removeDocument(f.id, docId)); } catch (e) { setError(e); } };
  const ordered = [...cats].sort((a, b) => Number(b.required) - Number(a.required));
  // Right to Rent evidence stays once approved: only Lotus Living removes it (the server refuses anyone else).
  const retained = (key: string) => (key === 'id' || key === 'rtr') && (f.reviewStatus ?? 'APPROVED') === 'APPROVED';
  return (
    <div className="space-y-3">
      <div className="grid gap-2 sm:grid-cols-2">
        {ordered.map((c) => {
          const docs = f.documents.filter((d) => d.category === c.key);
          return (
            <DocCard key={c.key} c={c} on={docs.length > 0} busy={busy} uploadable={canUpload && (canManage || c.who !== 'staff')} onFile={(file) => void up(c.key, file)}>
              {docs.map((d) => (
                <div key={d.id} className="mt-1.5 flex items-center gap-1.5 text-[11.5px]">
                  <FileText className="h-3.5 w-3.5 shrink-0 text-ink-400" />
                  <button type="button" onClick={() => void openStoredFile(d.url).catch((e) => setError(e))} className="min-w-0 flex-1 truncate text-left text-bronze-700 hover:underline">{d.name}</button>
                  <span className="shrink-0 text-[10.5px] text-ink-400">{d.uploadedBy ?? 'The tenant'} · {formatDate(d.uploadedAt)}</span>
                  {canUpload && (canManage || !retained(c.key)) && <button type="button" onClick={() => void del(d.id)} className="rounded p-0.5 text-ink-300 hover:text-red-500" aria-label={`Remove ${d.name}`}><Trash2 className="h-3.5 w-3.5" /></button>}
                </div>
              ))}
            </DocCard>
          );
        })}
      </div>
      <FormError error={error} />
    </div>
  );
}

/** One category's card: a drop target and a file picker when `uploadable` (clicks on its own buttons — open, remove — do not pick). */
function DocCard({ c, on, busy, uploadable, onFile, children }: {
  c: DocCategory; on: boolean; busy: string | null; uploadable: boolean; onFile: (file: File | undefined) => void; children: React.ReactNode;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const uploading = busy === c.key;
  const pick = () => { if (uploadable && !busy) input.current?.click(); };
  const tone = on ? 'border-emerald-200 bg-emerald-50' : c.required ? 'border-amber-200 bg-amber-50' : 'border-ink-200 bg-white';
  return (
    <div
      className={`group relative rounded-xl border px-3 py-2.5 outline-none ${tone} ${uploadable ? 'cursor-pointer focus-visible:ring-2 focus-visible:ring-bronze-300' : ''} ${over ? '!border-dashed !border-bronze-500 ring-2 ring-bronze-200' : ''}`}
      {...(uploadable ? {
        role: 'button', tabIndex: 0, 'aria-label': `${c.label}: upload a file`,
        onClick: (e: React.MouseEvent) => { if (!(e.target as HTMLElement).closest('button')) pick(); },
        onKeyDown: (e: React.KeyboardEvent) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); pick(); } },
        onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = busy ? 'none' : 'copy'; setOver(true); },
        onDragLeave: (e: React.DragEvent) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false); },
        onDrop: (e: React.DragEvent) => { e.preventDefault(); setOver(false); if (!busy) onFile(e.dataTransfer.files?.[0]); },
      } : {})}>
      <div className="flex items-center gap-2">
        <span className="flex-1 text-[12.5px] font-medium text-ink-800">{c.label}{c.required && !on && <i className="ml-1.5 text-[10.5px] font-normal not-italic text-amber-700">needed</i>}{!c.required && <i className="ml-1.5 text-[10.5px] font-normal not-italic text-ink-400">optional</i>}</span>
      </div>
      {uploadable && (
        <span className={`pointer-events-none absolute -top-2 right-3 inline-flex items-center gap-1 rounded-md border border-ink-200 bg-white px-1.5 py-px shadow-sm text-[10.5px] leading-4 text-ink-600 transition-opacity ${uploading || over ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100'}`}>
          <UploadIcon className="h-3 w-3" />{uploading ? 'Uploading…' : over ? 'Drop to upload' : on ? 'Add another' : 'Upload'}
          <input ref={input} type="file" accept="application/pdf,image/*" className="hidden" disabled={!!busy} onClick={(e) => e.stopPropagation()} onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ''; }} />
        </span>
      )}
      {c.hint && <p className="mt-0.5 text-[10.5px] text-ink-400">{c.hint}</p>}
      {children}
    </div>
  );
}

/**
 * The rent request on a flat (migration 050), read-only: the advert against what was asked, the %, the reason, the counter
 * and who decided. The decision itself is a step of the ApplicationDrawer (Decide the rent, or with Approve) — 26 Oct 2026.
 */
export function RentRequestInfo({ f }: { f: FlatFull }) {
  const st = f.rentRequestStatus;
  const advert = f.advertisedRentMinor ?? f.listRentMinor;
  const m = (v: number | null | undefined) => formatMoney(v ?? 0, f.currency);
  const decided = [f.rentDecidedBy, f.rentDecidedAt ? formatDate(f.rentDecidedAt) : null].filter(Boolean).join(' · ');
  return (
    <div className="rounded-lg border border-ink-200 bg-white px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-ink-500">Rent request</span>
        <RentChip r={{ ...f, needsHigherApproval: f.needsHigherApproval }} />
      </div>
      <div className="mt-1.5 grid gap-x-4 gap-y-0.5 text-[12.5px] text-ink-700 sm:grid-cols-3">
        <div>Advertised <b className="text-ink-900">{m(advert)}</b></div>
        <div>Asked <b className="text-ink-900">{m(f.requestedRentMinor)}</b> <span className="text-ink-500">· {f.requestedPercent ?? 0}% under</span></div>
        {f.rentCounterMinor !== null && <div>Counter <b className="text-ink-900">{m(f.rentCounterMinor)}</b></div>}
      </div>
      {f.rentRequestReason && <div className="mt-1 text-[12px] text-ink-600">Why: {f.rentRequestReason}</div>}
      {st !== 'PENDING' && decided && <div className="mt-1 text-[11px] text-ink-400">Decided {decided}{f.rentDecisionNote ? ` — ${f.rentDecisionNote}` : ''}</div>}
      {st === 'COUNTERED' && <div className="mt-1.5 text-[12px] text-sky-800">Waiting for the applicant to answer {m(f.rentCounterMinor)}.</div>}
      {st === 'PENDING' && <div className="mt-1 text-[11.5px] text-ink-500">Waiting for a decision — with the approval, or on its own (Actions → Asking rent).</div>}
    </div>
  );
}

const OUTCOME_LABEL: Record<ReferencingOutcome, string> = { PASS: 'Passed', PASS_WITH_GUARANTOR: 'Passed, guarantor needed', FAIL: 'Not passed' };
/** Goodlord's statuses as chips: grey while it is with the tenant, amber in review, green / red when decided. */
const chipTone = (status: string | null) => status === 'Accepted' ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
  : status === 'Rejected' ? 'border-red-200 bg-red-50 text-red-800'
    : status === 'Conditional Pass' ? 'border-amber-300 bg-amber-50 text-amber-800'
      : status === 'In Review' || status === 'Awaiting Response' ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-ink-200 bg-ink-50 text-ink-600';
export function GlChip({ status, label }: { status: string | null; label?: string }) {
  return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${chipTone(status)}`}>{label ?? status ?? 'Not sent'}</span>;
}

/**
 * Referencing (Goodlord): the status as Goodlord has it, the four sections, comments, when it was sent and the report.
 * Lotus Living sends it (automatic once approved and the holding deposit is in), checks now (if a webhook was missed) and adds a guarantor when Goodlord
 * asks for one; "Mark passed by hand (override)" stays on the tracker.
 */
export function GoodlordBlock({ f, canManage, pending, onSend, onSync, onGuarantor }: {
  f: FlatFull; canManage: boolean; pending: boolean; onSend: () => void; onSync: () => void; onGuarantor: (g: GuarantorBody) => void;
}) {
  const g = f.goodlord!;
  const [adding, setAdding] = useState(false);
  const [gu, setGu] = useState({ name: '', email: '', phone: '' });
  const report = f.documents.filter((d) => d.category === 'ref').slice(-1)[0];
  const [error, setError] = useState<unknown>(null);
  const done = f.steps.find((s) => s.key === 'refD')?.done;
  const guOk = gu.name.trim().split(/\s+/).length >= 2 && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(gu.email.trim());
  const canAddGuarantor = canManage && !!g.subjectId && (g.guarantorNeeded || (!g.guarantorSubjectId && g.outcome !== 'PASS'));
  return (
    <>
      <PartTitle title="Referencing (Goodlord)" />
      <div className="rounded-xl border border-ink-200 px-3.5 py-3 text-[12.5px]">
        <div className="flex flex-wrap items-center gap-2">
          <GlChip status={g.status} />
          {g.outcome && <GlChip status={g.outcome === 'PASS' ? 'Accepted' : g.outcome === 'FAIL' ? 'Rejected' : 'Conditional Pass'} label={OUTCOME_LABEL[g.outcome]} />}
          {done && <span className="text-[11.5px] text-emerald-700">Referencing marked passed by Lotus Living</span>}
          <span className="ml-auto text-[11.5px] text-ink-400">{g.sentAt ? `Sent ${formatDate(g.sentAt)}` : f.steps.find((s) => !s.auto)?.done ? 'Not sent yet' : 'Not sent yet — goes when the holding deposit lands'}{g.decidedAt ? ` · decided ${formatDate(g.decidedAt)}` : ''}</span>
        </div>
        {g.sections.length > 0 && (
          <div className="mt-2.5 grid gap-1.5 sm:grid-cols-4">
            {g.sections.map((s) => (
              <div key={s.name} className="rounded-lg border border-ink-100 px-2.5 py-1.5">
                <div className="text-[11px] text-ink-400">{s.name}</div>
                <div className="mt-0.5"><GlChip status={s.status} /></div>
                {s.comment && <div className="mt-0.5 text-[11px] text-ink-500">{s.comment}</div>}
              </div>
            ))}
          </div>
        )}
        {g.guarantor && <div className="mt-2 flex flex-wrap items-center gap-2 text-[11.5px] text-ink-600">Guarantor{g.guarantorDetails?.name ? ` ${g.guarantorDetails.name}` : ''}: <GlChip status={g.guarantor.status} /></div>}
        {g.comments && <p className="mt-2 text-[12px] text-ink-600">{g.comments}</p>}
        {g.outcome === 'FAIL' && <p className="mt-2 text-[12px] text-red-700">Goodlord did not pass this applicant. Reject the application, or mark it passed by hand if Lotus Living accepts the risk.</p>}
        {(canManage || report) && (
          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            {canManage && !g.sentAt && <Button size="sm" variant="accent" icon="Send" disabled={pending} onClick={onSend}>Send to Goodlord</Button>}
            {canManage && g.sentAt && <Button size="sm" variant="secondary" icon="RefreshCw" disabled={pending} onClick={onSync}>Check now</Button>}
            {report && <Button size="sm" variant="secondary" icon="FileText" onClick={() => void openStoredFile(report.url).catch((e) => setError(e))}>Open report</Button>}
            {canAddGuarantor && !adding && <Button size="sm" variant="secondary" icon="UserPlus" disabled={pending} onClick={() => setAdding(true)}>Add guarantor</Button>}
          </div>
        )}
        {adding && (
          <div className="mt-2.5 grid gap-2 sm:grid-cols-4">
            <Field label="Guarantor's full name"><Input value={gu.name} onChange={(e) => setGu({ ...gu, name: e.target.value })} className="w-full" /></Field>
            <Field label="Email"><Input type="email" value={gu.email} onChange={(e) => setGu({ ...gu, email: e.target.value })} className="w-full" /></Field>
            <Field label="Phone"><PhoneInput autoComplete="off" value={gu.phone} onChange={(phone) => setGu({ ...gu, phone })} /></Field>
            <div className="flex items-end gap-1.5">
              <Button size="sm" variant="accent" disabled={pending || !guOk} onClick={() => { onGuarantor({ name: gu.name.trim(), email: gu.email.trim(), phone: gu.phone.trim() || null }); setAdding(false); }}>Send</Button>
              <Button size="sm" variant="secondary" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        )}
        <FormError error={error} />
      </div>
    </>
  );
}

function PartTitle({ title, tag }: { title: string; tag?: string }) {
  return <div className="mb-2 mt-5 flex items-center gap-3 first:mt-0"><h4 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-500">{title}</h4><span className="h-px flex-1 bg-ink-100" />{tag && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] text-amber-700">{tag}</span>}</div>;
}
export function Note({ children, lock }: { children: React.ReactNode; lock?: boolean }) {
  return <div className="flex items-start gap-2 rounded-xl bg-ink-50 px-3 py-2 text-[12.5px] text-ink-600">{lock && <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-400" />}<span>{children}</span></div>;
}

/**
 * The tenant's page link as Actions menu items (3 Nov 2026 — the row of buttons under Next: moved into the footer's menu):
 * "Create the link" issues it (and emails it), "Open the tenant's page" opens it in a new tab (to see it or fill it in
 * with the tenant on the phone), "Copy link" copies it, and "Send email" emails the link again through the outbox
 * (Settings → Integrations says which transport; the log transport keeps it in the outbox). Shown once approved and not
 * handed over: anyone who can see the flat may open or copy the link; creating or sending it needs canSend.
 */
export function tenantLinkItems(f: FlatFull, { canSend, pending, onSend, onCopied }: { canSend: boolean; pending: boolean; onSend: () => void; onCopied: (url: string) => void }): MenuEntry[] {
  const p = f.portal;
  if (f.status === 'HANDED_OVER' || f.reviewStatus !== 'APPROVED' || !(p?.path || canSend)) return [];
  const path = p?.path && !p.expired ? p.path : null;
  const url = path ? `${window.location.origin}${path}` : null;
  const email = f.applicant?.email ?? null;
  const copy = async () => {
    if (!url) return;
    try { await navigator.clipboard.writeText(url); onCopied(url); }
    catch { window.prompt('Copy the tenant\'s link', url); }
  };
  return [
    !url && canSend && !!email && { label: p?.expired ? 'Create a fresh link' : 'Create the link', icon: 'Link', disabled: pending, onClick: onSend },
    url && { label: 'Open the tenant\'s page', icon: 'ExternalLink', href: url, title: url },
    url && { label: 'Copy link', icon: 'Copy', title: url, onClick: () => void copy() },
    url && canSend && { label: 'Send email', icon: 'Mail', disabled: pending || !email, title: email ? `Email the link to ${email}` : 'The tenant has no email address', onClick: onSend },
  ];
}

/**
 * The move-in checklist before hand-over (3 Nov 2026 — in place of the "opens here once the flat is handed over" note): the
 * region's MOVE_IN template (GET /checklists/templates, checklists.read) laid out as MoveInList lays it out, greyed and not
 * clickable, without the roadmap's own steps. The region is the sheet's (UK when the flat is on no sheet); a template for
 * no region is the fallback, as the API's templateFor does.
 */
export function MoveInPreview({ f, region }: { f: FlatFull; region: string | null | undefined }) {
  const q = useQuery({ queryKey: ['/checklists/templates'], queryFn: checklistsApi.templates, staleTime: 10 * 60_000 });
  const moveIn = (q.data ?? []).filter((t) => t.kind === 'MOVE_IN');
  const tpl = moveIn.find((t) => t.region === (region || 'UK')) ?? moveIn.find((t) => t.region === null) ?? null;
  const items = (tpl?.stages ?? []).flatMap((s) => s.items).filter((i) => !f.steps.some((s) => s.key === i.key));
  return (
    <div>
      <p className="mb-2 text-[12.5px] text-ink-500">Opens once the flat is handed over.</p>
      {q.isLoading ? <p className="text-[12px] text-ink-400">Loading…</p> : (
        <div aria-disabled className="grid gap-1 opacity-50 sm:grid-cols-2">
          {items.map((i) => (
            <div key={i.key} title={i.hint ?? undefined} className="flex cursor-default items-center gap-2 px-1.5 py-1 text-[12px]">
              <span className="grid h-4 w-4 shrink-0 place-items-center rounded border border-ink-300 text-transparent"><Check className="h-3 w-3" /></span>
              <span className="flex-1 text-ink-400">{i.label}</span>
            </div>
          ))}
        </div>
      )}
      <FormError error={q.error} />
    </div>
  );
}

/**
 * The move-in checklist, tickable from the flat (checklists.update): a plain item ticks here; one that needs details
 * (a bay number, a date, meter readings) opens the tenancy's checklist where the form is.
 */
export function MoveInList({ f, canTick, onChange }: { f: FlatFull; canTick: boolean; onChange: (r: FlatFull) => void }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: async ({ key, done }: { key: string; done: boolean }) => { await checklistsApi.tick(f.moveIn!.checklistId, key, { done }); return reservationsApi.get(f.id); },
    onSuccess: (r) => { onChange(r); void qc.invalidateQueries({ queryKey: ['/checklists'] }); void qc.invalidateQueries({ queryKey: ['moving'] }); },
  });
  const link = f.tenancy ? `/app/tenancies/${f.tenancy.id}?checklist=in` : null;
  return (
    <div>
      <div className="grid gap-1 sm:grid-cols-2">
        {f.moveIn!.items.filter((i) => !f.steps.some((s) => s.key === i.key)).map((i) => {
          const clickable = canTick && !i.needsDetails;
          const inner = <>
            <span className={`grid h-4 w-4 shrink-0 place-items-center rounded ${i.done ? 'bg-emerald-500 text-on-primary' : 'border border-ink-300 text-transparent'}`}><Check className="h-3 w-3" /></span>
            <span className={`flex-1 ${i.done ? 'text-ink-500 line-through decoration-ink-300' : 'text-ink-700'}`}>{i.label}{i.na && <span className="ml-1 text-[10.5px] text-ink-400">not needed</span>}</span>
            {canTick && i.needsDetails && !i.done && link && <Link to={link} className="shrink-0 text-[11px] text-bronze-700 hover:underline" onClick={(e) => e.stopPropagation()}>needs details</Link>}
          </>;
          return clickable
            ? <button key={i.key} type="button" disabled={m.isPending} title={i.hint ?? undefined} onClick={() => m.mutate({ key: i.key, done: !i.done })} className="flex items-center gap-2 rounded-lg px-1.5 py-1 text-left text-[12px] hover:bg-ink-50">{inner}</button>
            : <div key={i.key} title={i.hint ?? undefined} className="flex items-center gap-2 px-1.5 py-1 text-[12px]">{inner}</div>;
        })}
      </div>
      <FormError error={m.error} />
      {link && <Link to={link} className="mt-2 inline-block text-[12px] text-bronze-700 hover:underline">Open the full move-in checklist</Link>}
    </div>
  );
}
